import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { PlusCircle, CalendarClock, ClipboardList, Hourglass, Wallet, UserPlus, HeartHandshake, DollarSign, Package, UserCog, LayoutDashboard } from "lucide-react";
import SelectPraticanteDialog from "@/components/SelectPraticanteDialog";

const AVAL_ACTION = "__nova_avaliacao__";
const EVOL_ACTION = "__nova_evolucao__";

const SHORTCUTS_BY_ROLE = {
  admin: [
    { to: "/praticantes?novo=1", label: "Novo praticante", icon: HeartHandshake, tone: "turquoise" },
    { to: AVAL_ACTION, label: "Nova avaliação", icon: PlusCircle, tone: "lilac" },
    { to: "/agenda", label: "Ver agenda", icon: CalendarClock, tone: "gold" },
    { to: EVOL_ACTION, label: "Nova evolução", icon: ClipboardList, tone: "pink" },
    { to: "/financeiro", label: "Financeiro", icon: Wallet, tone: "emerald" },
    { to: "/usuarios", label: "Usuários", icon: UserPlus, tone: "slate" },
  ],
  recepcionista: [
    { to: "/agenda", label: "Novo agendamento", icon: CalendarClock, tone: "turquoise" },
    { to: AVAL_ACTION, label: "Nova avaliação", icon: PlusCircle, tone: "lilac" },
    { to: "/praticantes?novo=1", label: "Novo cadastro", icon: HeartHandshake, tone: "gold" },
    { to: "/lista-espera", label: "Lista de espera", icon: Hourglass, tone: "pink" },
    { to: "/pagamentos", label: "Baixa de pagamento", icon: DollarSign, tone: "emerald" },
  ],
  terapeuta: [
    { to: EVOL_ACTION, label: "Nova evolução", icon: ClipboardList, tone: "lilac" },
    { to: AVAL_ACTION, label: "Nova avaliação", icon: PlusCircle, tone: "turquoise" },
    { to: "/agenda", label: "Agenda do dia", icon: CalendarClock, tone: "gold" },
    { to: "/praticantes", label: "Praticantes", icon: LayoutDashboard, tone: "emerald" },
  ],
  financeiro: [
    { to: "/financeiro", label: "Registrar pagamento", icon: DollarSign, tone: "turquoise" },
    { to: "/financeiro", label: "Pendências do mês", icon: Wallet, tone: "amber" },
    { to: "/financeiro", label: "Pagar terapeuta", icon: UserCog, tone: "lilac" },
    { to: "/relatorios", label: "Relatórios", icon: LayoutDashboard, tone: "emerald" },
  ],
};

const TONE_MAP = {
  turquoise: "bg-[#DFF4F5] text-[#3A9CA2] hover:bg-[#CCEBED]",
  gold: "bg-[#FBEEDA] text-[#B98A3E] hover:bg-[#F6E1BE]",
  pink: "bg-[#FBE1EC] text-[#C4547E] hover:bg-[#F7CDDD]",
  lilac: "bg-[#EDE1F3] text-[#7B569A] hover:bg-[#E1CFEC]",
  emerald: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
  amber: "bg-amber-50 text-amber-700 hover:bg-amber-100",
  slate: "bg-slate-100 text-slate-700 hover:bg-slate-200",
};

export default function DashboardShortcuts({ role }) {
  const navigate = useNavigate();
  const [avalOpen, setAvalOpen] = useState(false);
  const [evolOpen, setEvolOpen] = useState(false);
  const shortcuts = SHORTCUTS_BY_ROLE[role] || SHORTCUTS_BY_ROLE.admin;

  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
        Atalhos rápidos
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {shortcuts.map((s) => {
          const Icon = s.icon;
          const onClick = () => {
            if (s.to === AVAL_ACTION) setAvalOpen(true);
            else if (s.to === EVOL_ACTION) setEvolOpen(true);
            else navigate(s.to);
          };
          return (
            <button
              key={s.label}
              onClick={onClick}
              data-testid={`shortcut-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex flex-col items-start gap-2 p-4 rounded-xl border border-transparent transition-all shadow-sm hover:shadow-md text-left ${TONE_MAP[s.tone]}`}
            >
              <Icon className="h-5 w-5" />
              <span className="font-semibold text-sm">{s.label}</span>
            </button>
          );
        })}
      </div>

      <SelectPraticanteDialog
        open={avalOpen}
        onOpenChange={setAvalOpen}
        icon={ClipboardList}
        iconBg="bg-[#EDE1F3]"
        iconColor="text-[#7B569A]"
        title="Nova avaliação"
        description={<>Selecione o praticante para abrir a <b>Ficha de Avaliação Clínica</b>.</>}
        ctaLabel="Abrir ficha"
        ctaClass="bg-brand-lilac hover:bg-[#9670B0] text-white"
        onSelect={(p) => navigate(`/praticantes/${p.id}/ficha`)}
        novoPraticanteQuery="tipo=em_avaliacao"
        testIdPrefix="nova-avaliacao"
      />

      <SelectPraticanteDialog
        open={evolOpen}
        onOpenChange={setEvolOpen}
        icon={ClipboardList}
        iconBg="bg-[#FBE1EC]"
        iconColor="text-[#C4547E]"
        title="Nova evolução"
        description={<>Selecione o praticante para registrar a <b>evolução clínica</b>.</>}
        ctaLabel="Registrar evolução"
        ctaClass="bg-[#C4547E] hover:bg-[#A63E68] text-white"
        onSelect={(p) => navigate(`/evolucoes?praticante_id=${p.id}&novo=1`)}
        testIdPrefix="nova-evolucao"
      />
    </div>
  );
}
